import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
//api
import { deleteSchedule } from 'api/company';
import { getAllSchedules } from 'api/pathForHook/company';
//helpers
import { formatDateToSpanish } from 'helpers';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
//components
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Title from 'components/backoffice/texts/title';
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import FiltersTableSchedules from './FiltersTableSchedules';
import StatusBudget from 'components/budget/StatusBudget';

const Schedules = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [listSchedules, setListSchedules] = useState([]);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [idItem, setIdItem] = useState();
  const [form, setForm] = useState({});
  const [page, setPage] = useState(1);
  const [responseInfo, setResponseInfo] = useState({});
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext()

  const colsSchedules = ['I.D', t('titulo'), t('state'), t('inicioDos'), t('completion'), t('actions')];

  const { fetchData, stateData, loading } = useHandleSearch();

  const getListSchedules = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        title: form.title || '',
        status: form.status || '',
        fields: 'id,title,status,start_date,end_date'
      }
      await fetchData(getAllSchedules(), payload);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    setTitle(t('agenda'))
    return () => {
      setTitle('');
    };
  }, [setTitle])

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setListSchedules(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setListSchedules(null);
      setResponseInfo({ errors });
    }
  }, [stateData]);

  useEffect(() => {
    getListSchedules();
  }, [page, form]);

  const handleClear = () => {
    setForm({
      title: '',
      status: '',
    });
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }))
  };

  const handleCreate = () => {
    navigate('/intranet/schedules/create');
  };

  const handleEdit = (id) => {
    navigate(`/intranet/schedules/edit/${id}`);
  };

  const handleDelete = (item, id, setOpenModal) => {
    if (item.id === id) {
      setOpenModal(true);
      setIdItem(id);
    }
  };

  const scheduleDelete = useCallback(async () => {
    try {
      const { status } = await deleteSchedule(idItem);
      if (status === 204) {
        toast.success(t('eliminadocorrectamente'));
        setOpenModalDelete(false);
        setForm({
          title: '',
          status: '',
        });
      } else {
        toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
      }
    } catch (error) {
      console.log(error);
    }
  }, [openModalDelete]);

  const validateStatus = (item) => {
    if (item === 'draft') {
      return t('draft');
    } else if (item === 'published') {
      return t('published');
    } else {
      return t('hidden');
    }
  };

  const serializerStatusSchedule = (status) => {
    if (status === 'published') {
      return 'success';
    } else if (status === 'draft') {
      return 'warning';
    } else if (status === 'hidden') {
      return 'danger';
    }
    return 'info';
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('agenda')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col xl={9} lg={12} md={12} xs={12} className="d-flex text-start">
          <FiltersTableSchedules
            form={form}
            setFormFilter={setForm}
            handleClear={handleClear}
            t={t}
            page={page}
            setPage={setPage}
          />
        </Col>
        <Col xl={3} lg={12} md={12} xs={12} className="d-flex text-end align-bottom p-xl-0 justify-content-end pt-5 pt-xl-0 gap-3">
          {userPermissions.includes('typeschedule.__view') && (
            <Button extraClass="py-1 mx-3" color={'primary'} onClick={() => navigate('/intranet/type-schedules')}>
              {t('typeSchedules')}
            </Button>
          )}
          {userPermissions.includes('schedule.__add') && (
            <Button color={'primary'} onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <CustomTable
        cols={colsSchedules}
        isLoading={loading}
        page={page}
        noResults={responseInfo?.noResults}
        changePage={setPage}
        totalPages={responseInfo?.totalPages}
      >
        {listSchedules &&
          listSchedules.map((listSchedule, index) => {
            return (
              <tr key={listSchedule.id}>
                <td>{`#${listSchedule.id}`}</td>
                <td>{listSchedule.title}</td>
                <td>
                  <div className="d-flex justify-content-center">
                    <StatusBudget
                      text={validateStatus(listSchedule.status)}
                      status={serializerStatusSchedule(listSchedule.status)}
                    />
                  </div>
                </td>
                <td>{formatDateToSpanish(listSchedule.start_date)}</td>
                <td>{formatDateToSpanish(listSchedule.end_date)}</td>
                <td className="item-dropdown text-end">
                  <DropdownIcons
                    idItem={index}
                    handleEdit={userPermissions.includes('schedule.__modify') && (() => handleEdit(listSchedule.id))}
                    handleDelete={
                      userPermissions.includes('schedule.__delete') &&
                      (() => handleDelete(listSchedule, listSchedule.id, setOpenModalDelete))
                    }
                  />
                </td>
              </tr>
            );
          })}
      </CustomTable>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={scheduleDelete}
        deleteTitle={t('deleteschedule')}
        beforeTitle={t('agenda')}
      />
    </div>
  );
};

export default Schedules;
